import React, { useState } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Box,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { marriageLessons } from '../data/marriageLessons';
import { MarriageLesson } from '../types';
import Seo from './Seo';

const MarriageLessons = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');

  const categories = [
    'All',
    'Communication',
    'Conflict Resolution',
    'Intimacy',
    'Finances',
    'Parenting',
    'Spiritual Growth',
    'Other',
  ];

  const filteredLessons = marriageLessons.filter((lesson: MarriageLesson) => {
    const matchesSearch =
      lesson.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lesson.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      categoryFilter === 'All' || lesson.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const handleLessonClick = (lessonId: string) => {
    navigate(`/marriage/lessons/${lessonId}`);
  };

  return (
    <Container maxWidth="lg">
      <Seo
        title="Marriage Ministry Lessons"
        description="Strengthen your marriage with biblical lessons and practical guidance from the Marriage Ministry at New Mt. Zion Missionary Baptist Church."
        path="/marriage/lessons"
      />
      {/* Header */}
      <Box sx={{ mt: 2, mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Marriage Ministry Lessons
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
          Strengthen your marriage with our biblical lessons and practical
          guidance
        </Typography>

        {/* Back Button */}
        <Button
          variant="outlined"
          onClick={() => navigate('/marriage')}
          sx={{ mb: 3 }}
        >
          ← Back to Marriage Ministry
        </Button>
      </Box>

      {/* Search and Filter Controls */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Search lessons..."
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                label="Category"
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {/* Results Count */}
      <Typography variant="body1" sx={{ mb: 3 }}>
        Showing {filteredLessons.length} lesson
        {filteredLessons.length !== 1 ? 's' : ''}
      </Typography>

      {/* Lessons Grid */}
      <Grid container spacing={3}>
        {filteredLessons.map((lesson: MarriageLesson) => (
          <Grid item xs={12} sm={6} md={4} key={lesson.id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6,
                },
              }}
              onClick={() => handleLessonClick(lesson.id)}
            >
              <CardMedia
                component="img"
                height="200"
                image={
                  lesson.imageUrl ||
                  'https://images.unsplash.com/photo-1511895426328-dc8714191300?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80'
                }
                alt={lesson.title}
                sx={{ backgroundColor: '#f5f5f5' }}
              />
              <CardContent sx={{ flexGrow: 1, p: 2 }}>
                <Typography variant="h6" component="h3" gutterBottom>
                  {lesson.title}
                </Typography>

                <Typography variant="body2" color="text.secondary" paragraph>
                  {lesson.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* No Results */}
      {filteredLessons.length === 0 && (
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography variant="h6" color="text.secondary">
            No lessons found matching your search criteria.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => {
              setSearchTerm('');
              setCategoryFilter('All');
            }}
            sx={{ mt: 2 }}
          >
            Clear Filters
          </Button>
        </Box>
      )}
    </Container>
  );
};

export default MarriageLessons;
